import React, {useState} from "react";

import i18n from "../../../i18n";

export default function AudioElement({ item }) {

    // Load the logo
    const [photoFile, setPhotoFile] = useState(null);
    const [mediaFile, setMediaFile] = useState(null);
    const [mimeType, setMimeType] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [seekStartTime, setSeekStartTime] = useState(null);
    const [playStartTime, setPlayStartTime] = useState(null);
    const [totalPlayedTime, setTotalPlayedTime] = useState(0);
    const audioRef = React.useRef(null);

    // Add a debounce timeout variable
    const [seekDebounceTimeout, setSeekDebounceTimeout] = useState(null);

    React.useEffect(() => {
        const fetchMediaData = () => {
            let mimeTypeName = item.data['media'][i18n.language]['mime_type'] ? item.data['media'][i18n.language]['mime_type']['name'] : null;
            let mimeType = 'audio/'+mimeTypeName;
            setMimeType(mimeType);
            if (mimeTypeName && item.data['media'] && item.data['media'][i18n.language]) {
                setMediaFile( item.data['media'] && item.data['media'][i18n.language].preview_url);
            }
        }

        if (item.data['photo']) {
            setPhotoFile(item.data['photo'].preview_url);
        }
        if (item.data['media'] && item.data['media'][i18n.language]) {
            fetchMediaData();
        }

    }, [item])

    // Handle audio events
    const handlePlay = () => {
        setPlayStartTime(Date.now());
        const audioName = item.data['media']?.[i18n.language]?.name || 'Untitled';
        console.log(`Audio played: "${audioName}" at ${formatTime(currentTime)}`);
        
        // Matomo tracking
        var _paq = window._paq = window._paq || [];
        _paq.push(['trackEvent', 'Audio', 'Play', audioName, currentTime]);
    };

    const handlePause = () => {
        if (playStartTime) {
            const sessionPlayTime = Math.floor((Date.now() - playStartTime) / 1000);
            const newTotalPlayedTime = totalPlayedTime + sessionPlayTime;
            setTotalPlayedTime(newTotalPlayedTime);
            const audioName = item.data['media']?.[i18n.language]?.name || 'Untitled';
            console.log(`Audio paused: "${audioName}" at ${formatTime(currentTime)}, session played time: ${formatTime(sessionPlayTime)}, total played time: ${formatTime(newTotalPlayedTime)}`);
            setPlayStartTime(null);
            
            // Matomo tracking with custom dimensions
            var _paq = window._paq = window._paq || [];
            _paq.push(['setCustomDimension', 1, currentTime.toString()]);
            _paq.push(['setCustomDimension', 2, sessionPlayTime.toString()]);
            _paq.push(['setCustomDimension', 3, newTotalPlayedTime.toString()]);
            _paq.push(['trackEvent', 'Audio', 'Pause', audioName, currentTime]);
        }
    };

    const handleTimeUpdate = () => {
        setCurrentTime(audioRef.current.currentTime);
    };

    const handleSeeking = () => {
        // If we're currently playing, we need to account for the time played so far
        if (playStartTime) {
            const sessionPlayTime = Math.floor((Date.now() - playStartTime) / 1000);
            setTotalPlayedTime(totalPlayedTime + sessionPlayTime);
            setPlayStartTime(null); // Reset play start time as we're seeking
        }
        
        // Only set the seek start time once at the beginning of a seek operation
        if (seekStartTime === null) {
            setSeekStartTime(currentTime);
            
            // Clear any existing timeout to prevent multiple seek end events
            if (seekDebounceTimeout) {
                clearTimeout(seekDebounceTimeout);
                setSeekDebounceTimeout(null);
            }
        }
    };

    const handleSeeked = () => {
        // Use debouncing to only track the final seek position
        if (seekStartTime !== null) {
            // Clear any existing timeout
            if (seekDebounceTimeout) {
                clearTimeout(seekDebounceTimeout);
            }
            
            // Set a new timeout to track the seek after a short delay
            const timeout = setTimeout(() => {
                const audioName = item.data['media']?.[i18n.language]?.name || 'Untitled';
                const fromTime = seekStartTime;  // Raw seconds
                const toTime = audioRef.current.currentTime;  // Raw seconds
                console.log(`Audio seeked: from ${formatTime(fromTime)} to ${formatTime(toTime)}`);
                
                // Matomo tracking with raw seconds values
                var _paq = window._paq = window._paq || [];
                _paq.push(['trackEvent', 'Audio', 'Seek', audioName, toTime]);
                _paq.push(['setCustomDimension', 1, fromTime]);  // Raw seconds for starting position
                _paq.push(['setCustomDimension', 2, toTime]);    // Raw seconds for ending position
                
                setSeekStartTime(null);
                setSeekDebounceTimeout(null);
                
                // If the audio is still playing after seeking, restart the play timer
                if (!audioRef.current.paused) {
                    setPlayStartTime(Date.now());
                }
            }, 500); // 500ms debounce time
            
            setSeekDebounceTimeout(timeout);
        }
    };

    // Helper function to format time in MM:SS format
    const formatTime = (timeInSeconds) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div className="">
            {item && i18n.language &&
                <>

                    {photoFile &&
                        <div>
                            { (window.location.hostname === "denhaag-ctrl.tourio.com" || window.location.hostname === "denhaag-ctrl.dev.tourio.com") ? (
                                <img
                                    src={photoFile}
                                    className="w-40 m-auto mb-4"/>
                            ) : (
                                <img
                                    src={photoFile}
                                    className="m-auto mb-4"/>
                            )
                            }
                        </div>
                    }

                    { mediaFile && mimeType &&
                        <div>
                            <audio 
                                ref={audioRef}
                                controls 
                                className="m-auto w-full md:w-[300px]" 
                                autoPlay={false}
                                onPlay={handlePlay}
                                onPause={handlePause}
                                onTimeUpdate={handleTimeUpdate}
                                onSeeking={handleSeeking}
                                onSeeked={handleSeeked}
                            >
                                <source src={ mediaFile } type={ mimeType } />
                            </audio>
                        </div>
                    }

                    { item.data['title'] && item.data['title'][i18n.language] &&
                        <div className="hidden text-center text-sm">
                            {item.data['title'][i18n.language]}
                        </div>
                    }



                </>
            }
        </div>
    )
}
